import moment from "moment-timezone";
export const wait = async (ms: number) =>
  new Promise<void>(resolve => setTimeout(resolve, ms));

// Use for lazy singleton setup like SDK clients, where concurrent callers
// should share one initialization attempt without blocking future retries.
export function memoizeAsyncFactory<T>(factory: () => Promise<T>) {
  let promise: Promise<T> | undefined;

  return function get() {
    promise ??= factory().catch((error: unknown) => {
      promise = undefined;
      throw error;
    });

    return promise;
  };
}

type PollWithBackoffOptions<T> = {
  fn: () => Promise<T>;
  timeout: number; // ms
  minDelay: number; // ms
  backoffFactor?: number;
  shouldRetry?: (error: unknown) => boolean;
};

const DEFAULT_BACKOFF_BASE = 2;

export async function pollWithBackoff<T>(options: PollWithBackoffOptions<T>): Promise<T> {
  const { fn, timeout, minDelay, backoffFactor, shouldRetry } = {
    backoffFactor: DEFAULT_BACKOFF_BASE,
    shouldRetry: () => true,
    ...options,
  };

  const startTime = moment.utc().valueOf();
  let attempt = 0;
  let elapsedMs = 0;

  while (elapsedMs < timeout) {
    try {
      return await fn();
    } catch (error) {
      if (!shouldRetry(error)) {
        throw error;
      }
    }

    const remainingMs = timeout - elapsedMs;
    if (remainingMs > 0) {
      const delayMs = Math.min(minDelay * backoffFactor ** attempt, remainingMs);
      await wait(delayMs);
    }

    attempt += 1;
    elapsedMs = moment.utc().valueOf() - startTime;
  }

  throw new PollingTimeoutError();
}

export class PollingTimeoutError extends Error {}
